/**
 * FBX loader pipeline. Loads Autodesk FBX files with animations, skinning,
 * and embedded textures using Three.js FBXLoader. Returns a normalized
 * ModelResult type.
 */

import * as THREE from 'three/webgpu';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { Logger } from '../core/Logger.js';
import type { ModelResult, LoadOptions } from './ModelPipeline.js';

/* ------------------------------------------------------------------ */
/*  FBXPipeline                                                        */
/* ------------------------------------------------------------------ */

export class FBXPipeline {
  private readonly loader: FBXLoader;

  constructor() {
    this.loader = new FBXLoader();
  }

  /**
   * Load an FBX file and return a normalized ModelResult.
   */
  async load(url: string, options?: LoadOptions): Promise<ModelResult> {
    return new Promise<ModelResult>((resolve, reject) => {
      this.loader.load(
        url,
        (group) => {
          Logger.debug('Assets', `FBX loaded: ${url}`);

          // FBX stores animations on the root group
          const animations: THREE.AnimationClip[] =
            (group as unknown as Record<string, unknown>).animations as THREE.AnimationClip[] ?? [];

          // Extract cameras by traversing the scene graph
          const cameras: THREE.Camera[] = [];
          group.traverse((child) => {
            if (child instanceof THREE.Camera) {
              cameras.push(child);
            }
          });

          resolve({
            scene: group,
            scenes: [group],
            cameras,
            animations,
            format: 'fbx',
            meta: {
              sourceUrl: url,
            },
          });
        },
        options?.onProgress,
        (error) => {
          Logger.error('Assets', `Failed to load FBX: ${url}`, error);
          reject(error);
        },
      );
    });
  }
}
